// ============================================
// narb-auth/src/accessibility/accessibilityStore.js
// ============================================
// Single source of truth for accessibility state across a Narbodica app.
// Reads from /api/preferences/accessibility on mount, writes back when
// the user changes settings.
//
// Design principle: the store is the contract between the app and
// accessibility behaviour. Any component that needs to know "is
// accessibility mode on?" or "what's the user's font-size preference?"
// reads from here. Any component that changes a setting writes here,
// and the store handles persisting to the backend.
//
// Pattern matches src/stores/voiceStore.js — same Zustand shape,
// same async-action handling, same loading-state convention.
// ============================================

import { create } from 'zustand';

// ============================================
// DEFAULTS
// ============================================
// What every user starts with if they have no saved preferences yet.
// Conservative defaults: accessibility-first behaviours OFF except
// for things that don't change behaviour (font size at 'medium' is
// the same as no preference set).
// ============================================
const DEFAULT_PREFS = {
  // Vision
  blindOrLowVision: false,
  screenReaderActive: false,
  fontSize: 'medium',           // 'small' | 'medium' | 'large' | 'extra-large'
  dyslexicFont: false,
  highContrast: false,

  // Motion
  reduceMotion: false,

  // Motor
  largerTapTargets: false,
  disableSwipeGestures: false,

  // Audio
  ttsEnabled: false,            // text-to-speech reads UI aloud

  // Cognitive
  customNeeds: '',              // free-text — user describes their needs

  // Meta
  onboardingComplete: false,    // user has explicitly engaged with accessibility settings at least once
};

// ============================================
// STORE
// ============================================
export const useAccessibilityStore = create((set, get) => ({
  // === state ===
  enabled: false,            // is accessibility-first mode currently active?
  enabledReason: null,       // 'url_override' | 'screen_reader_detected' | 'saved_preference' | 'user_toggled' | null
  prefs: { ...DEFAULT_PREFS },
  loading: false,
  error: null,
  sheetOpen: false,          // is the AccessibilitySheet currently visible?
  hasInitialised: false,     // has detector run yet?

  // === actions ===

  /**
   * Called once by the app on mount, after the auth token is available.
   * Loads saved prefs from the backend, runs detection, applies CSS
   * variables to document.documentElement, and sets enabled state.
   *
   * Idempotent — safe to call twice; second call is a no-op.
   */
  initialise: async ({ token, apiBase, detectorResult } = {}) => {
    if (get().hasInitialised) return;

    set({ loading: true, error: null });

    try {
      // Pull saved prefs from backend if we have a token
      let savedPrefs = null;
      if (token && apiBase) {
        try {
          const response = await fetch(`${apiBase}/api/preferences/accessibility`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (response.ok) {
            const data = await response.json();
            // Backend returns { success, data: { ...prefs } } pattern
            savedPrefs = data?.data || null;
          }
        } catch (err) {
          console.warn('[accessibility] Could not load saved prefs:', err.message);
        }
      }

      // Merge: defaults <- saved <- detector hints
      const merged = {
        ...DEFAULT_PREFS,
        ...(savedPrefs || {}),
      };

      // If detector found a screen reader, surface that in the prefs
      // (without persisting yet — we let the user explicitly opt in
      // by interacting with the sheet)
      if (detectorResult?.mode === 'on') {
        merged.screenReaderActive = merged.screenReaderActive || detectorResult.reason === 'screen_reader_detected';
      }

      // Apply CSS variables to document root immediately so the
      // first render reflects the user's preferences
      applyAccessibilityCssVars(merged);

      set({
        prefs: merged,
        enabled: detectorResult?.mode === 'on',
        enabledReason: detectorResult?.mode === 'on' ? detectorResult.reason : null,
        hasInitialised: true,
        loading: false,
      });
    } catch (err) {
      console.error('[accessibility] Initialise failed:', err);
      set({ error: err.message, loading: false, hasInitialised: true });
    }
  },

  /**
   * User explicitly toggled accessibility mode on/off via the toggle
   * button. Persists immediately so the preference survives reload.
   */
  setEnabled: async (enabled, { token, apiBase } = {}) => {
    set({ enabled, enabledReason: enabled ? 'user_toggled' : null });

    // Mark onboarding as complete on first explicit interaction
    const prefs = { ...get().prefs, onboardingComplete: true };
    set({ prefs });
    applyAccessibilityCssVars(prefs);

    if (token && apiBase) {
      try {
        await fetch(`${apiBase}/api/preferences/accessibility`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(prefs),
        });
      } catch (err) {
        console.warn('[accessibility] Could not persist toggle:', err.message);
      }
    }
  },

  /**
   * Update a single preference. Used by the AccessibilitySheet for
   * each control. Persists with debouncing handled at component level
   * (we trust the caller to debounce free-text fields).
   */
  updatePref: async (key, value, { token, apiBase } = {}) => {
    const prefs = { ...get().prefs, [key]: value, onboardingComplete: true };
    set({ prefs });
    applyAccessibilityCssVars(prefs);

    if (token && apiBase) {
      try {
        await fetch(`${apiBase}/api/preferences/accessibility`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(prefs),
        });
      } catch (err) {
        console.warn(`[accessibility] Could not persist ${key}:`, err.message);
      }
    }
  },

  openSheet: () => set({ sheetOpen: true }),
  closeSheet: () => set({ sheetOpen: false }),
  toggleSheet: () => set({ sheetOpen: !get().sheetOpen }),
}));

// ============================================
// CSS VARIABLE + CLASS APPLICATION
// ============================================
// When prefs change, push CSS variables AND/OR classes to
// document.documentElement so any component using the standard
// accessibility hooks updates immediately. This is the bridge
// between "user preferences" and "what the UI actually looks like."
//
// Two patterns:
//   - CSS variables for GRADUATED values (font size multiplier, font
//     family) — the value itself encodes the state, no class needed.
//   - Classes for BOOLEAN toggles (high contrast, larger tap targets,
//     reduce motion) — easier to write CSS rules that activate only
//     when needed, without forcing every consumer to handle a
//     "default value" case.
//
// Rationale (2 May 2026 refactor): the original implementation set
// --a11y-tap-target-min to 40px when off and 56px when on, which
// would force 40px minimum on every button if any rule used the var,
// breaking compact UI elements (close buttons, icon buttons, etc).
// Class-based approach lets CSS rules be conditional and targeted.
//
// Each app's stylesheet should:
//   - Reference --a11y-font-size-multiplier when sizing text
//   - Reference --a11y-font-family when setting font-family
//   - Write rules scoped to .a11y-larger-tap-targets, .a11y-reduce-motion,
//     .a11y-high-contrast that activate only when those classes are on root
// ============================================
function applyAccessibilityCssVars(prefs) {
  if (typeof document === 'undefined') return; // SSR safety

  const root = document.documentElement;

  // Font size — multiplier on top of the app's base font size
  // Always set (multiplying by 1 is a no-op when at default 'medium')
  const fontSizeMultiplier = {
    'small': '0.9',
    'medium': '1',
    'large': '1.15',
    'extra-large': '1.3',
  }[prefs.fontSize] || '1';
  root.style.setProperty('--a11y-font-size-multiplier', fontSizeMultiplier);

  // Dyslexic font — apps can opt into using this var as their font-family
  // Always set ('inherit' when off so it's a no-op)
  root.style.setProperty(
    '--a11y-font-family',
    prefs.dyslexicFont
      ? '"OpenDyslexic", "Comic Sans MS", system-ui, sans-serif'
      : 'inherit'
  );

  // Boolean toggles — use classes on root, not CSS variables
  toggleRootClass(root, 'a11y-larger-tap-targets', prefs.largerTapTargets);
  toggleRootClass(root, 'a11y-reduce-motion', prefs.reduceMotion);
  toggleRootClass(root, 'a11y-high-contrast', prefs.highContrast);
  toggleRootClass(root, 'a11y-disable-swipe', prefs.disableSwipeGestures);
}

function toggleRootClass(root, className, on) {
  if (on) {
    root.classList.add(className);
  } else {
    root.classList.remove(className);
  }
}
